import { useEffect } from "react";
import { BaseAdaptor, IAdaptor } from "./baseAdaptor";
import { MiscUtil } from "../utils/miscUtils";
import * as Constants from "../constant/constants";

export interface ArkoseAdaptorProps {
  challengeSessionId: string;
  challengeId: string;
  publicKey: string;
  companyName: string;
  partnerName: string;
  encryptedBlob: string;
  theme: string;
  language: string;
  scroll: boolean;
  loading: (laoding: boolean) => void;
}

export class ArkoseAdaptor extends BaseAdaptor<ArkoseAdaptorProps> {
  private _challengeSessionId: string;
  private _publicKey: string;
  private _companyName: string;
  private _partnerName: string;
  private _encryptedBlob: string;
  private _theme: string;
  private _language: string;
  private _scroll: boolean;

  constructor(props: ArkoseAdaptorProps & IAdaptor) {
    super(props);

    this._challengeSessionId = this.props.challengeSessionId;
    this._publicKey = this.props.publicKey;
    this._companyName = this.props.companyName;
    this._partnerName = this.props.partnerName;
    this._encryptedBlob = this.props.encryptedBlob;
    this._theme = this.props.theme;
    this._language = this.props.language;
    this._scroll = this.props.scroll;
  }

  // scroll to challenge on initial render
  componentDidMount(): void {
    // disable scroll if _scroll config is set to false;
    if (!this._scroll) {
      return;
    }

    const ec = document.getElementById("arkose-ec");

    if (ec) {
      // Scroll to the Arkose Iframe.
      const checkIframe = setInterval(() => {
        ec.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // ensure the element has been scrolled into view before clearing the interval.
        if (ec.getBoundingClientRect().top === window.innerHeight / 2) {
          clearInterval(checkIframe);
        }
      }, 100);
    } 
  }

  onCompleted(data: any): void {
    this.props.onCompleted({
      token: data.token,
      sessionId: this._challengeSessionId,
      encryptedBlob: this._encryptedBlob,
    });
  }

  onFailed(data: any): void {
    this.props.onFailed({
      token: data.token,
      sessionId: this._challengeSessionId,
      encryptedBlob: this._encryptedBlob,
    });
  }

  public onInit = (): JSX.Element => {
    const ArkoseLabs = () => {
      const createArkoseScript = () => {
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.src = `https://${this._companyName}-api.arkoselabs.com/v2/${this._publicKey}/api.js`;
        script.setAttribute("data-callback", "setupEnforcement");
        script.async = true;
        script.defer = true;
        script.id = "arkose-script";

        document.head.append(script);

        return () => {
          const object = document.getElementById("arkose-script");
          if (object) {
            object.remove();
          }
        };
      };

      // We only want to have the API script tag created once
      useEffect(() => {
        return createArkoseScript();
        // eslint-disable-next-line
      }, []);

      return <div id="arkose-ec"></div>;
    };

    const ArkoseLocaleMapper = (locale: string): string => {
      const hasMarketLocale = (data: string): boolean => data.indexOf("-") !== -1;

      // if the locale is empty or null, return default locale.
      if (MiscUtil.isNullOrUndefinedOrEmptyString(locale)) {
        return Constants.defaultLocale;
      }

      const partnerLocale = locale.toLowerCase();
      // return the partner locale if it exists in the arkose locale list
      if (Constants.arkoseLocaleList.indexOf(partnerLocale) !== -1) {
        return partnerLocale;
      } else {
        if (hasMarketLocale(locale)) {
          // strip the market locale and search the primary locale in arkose locale list to find candidates
          const primaryLocale = partnerLocale.split("-")[0];
          const candidates: string[] = [];
          Constants.arkoseLocaleList.forEach(loc => {
            // if the primary locale matches, then add to the candidate list
            if (loc.indexOf(primaryLocale) > -1) {
              candidates.push(loc)
            }
          })

          if (candidates.length === 0) {
            return Constants.defaultLocale;
          }

          // TODO: sort the candidate locales by its length in ascending order, thus return the shortest locale if multiple candidates exists. 
          // Need to confirm if this is appropriate.
          candidates.sort((a, b) => a.length - b.length);
          return candidates[0];
        }
      }

      return Constants.defaultLocale;
    };
    // Create the setup function that the Arkose Labs API will use to configure it's use
    // and the callbacks that it will trigger
    const setupEnforcement = (enforcementObject: any) => {
      enforcementObject.setConfig({
        data: {
          blob: this._encryptedBlob,
        },
        selector: "#arkose-ec",
        mode: "inline",
        onFailed: (response: any) => {
          this.onFailed(response);

          // reset Arkose EC after failed event is logged in CMS
          enforcementObject.reset();
        },
        onCompleted: (response: any) => {
          this.onCompleted(response);
        },
        onShow: () => {
          this.props.loading(false);
        },
        styleTheme: this._theme,
        // TODO: enable the locale mapper once Arkose team verify the button text missing issue is fixed.
        // language: ArkoseLocaleMapper(this._language),
        language: this._language,
        accessibilitySettings: {
          grabFocusToInline: true,
        },
        onShown: (response: any) => {
          const ec = document.getElementsByTagName("iframe")[0];

          if (ec) {
            setTimeout(() => {
              ec.focus();
            }, 500);
          }
        },
      });
    };

    // Make the setup function a global variable so that the Arkose Labs API can run it
    // once the API has loaded. The name of this variable MUST match the name of the setup
    // function defined in the ArkoseLabs component 'data-callback' attribute
    (window as any).setupEnforcement = setupEnforcement;

    return (
      <div className="container">
        <div className="row justify-content-center">
          <ArkoseLabs />
        </div>
      </div>
    );
  };

  render() {
    return (
      <BaseAdaptor
        onCompleted={this.onCompleted}
        onFailed={this.onFailed}
        onInit={this.onInit}
      />
    );
  }
}
